import React from 'react';
import PropTypes from 'prop-types';
import TicketsSection from './TicketsSection';
import BudgetLineItemSection from './BudgetLineItemSection';
import useIsMobile from '../../shared/hooks/useIsMobile';

const IncomeSection = ({
  csrfToken,
  team,
  currentUser,
  offer,
  budget,
  calculated,
  updateBudget,
  onPassThroughChange,
  createTicketBudgetLineItem,
  createIncomeBudgetLineItem,
  createAncillaryIncomeBudgetLineItem,
  createBeforeAdjustedDeductionsBudgetLineItem,
  createAdditionBudgetLineItem,
  deleteBudgetLineItem,
  autoSaveBudgetLineItem,
  formatCalculatedNumber,
  financeCategories = [],
  onFinanceCategoryAdded,
  settlementTickets = [],
  addOnActuals = {},
  performers = [],
  onVisibilityChange,
  showCurrencyConversion,
  globalCurrency,
  reorderLineItem,
  onReorderLineItems,
  allLineItems,
  mobileOfferColumnOnly = false
}) => {
  const isMobile = useIsMobile();
  const effectiveBudget = (isMobile && mobileOfferColumnOnly)
    ? {
        ...budget,
        // Force the Offer column visible — mobile-offer-only mode is meaningless
        // if a user-saved preference would also hide it.
        tickets_show_offer: true,
        other_income_show_offer: true,
        before_adjusted_deductions_show_offer: true,
        additions_show_offer: true,
        tickets_show_forecast: false,
        tickets_show_actuals: false,
        other_income_show_forecast: false,
        other_income_show_actuals: false,
        before_adjusted_deductions_show_forecast: false,
        before_adjusted_deductions_show_actuals: false,
        additions_show_forecast: false,
        additions_show_actuals: false,
        ancillary_income_show_forecast: false,
        ancillary_income_show_actuals: false,
      }
    : budget;
  const ticketsExpanded = effectiveBudget.tickets_section_expanded !== false;
  const otherIncomeExpanded = effectiveBudget.other_income_section_expanded !== false;
  const beforeAdjustedDeductionsExpanded =
    effectiveBudget.before_adjusted_deductions_section_expanded !== false;
  const additionsExpanded = effectiveBudget.additions_section_expanded !== false;
  const ancillaryIncomeExpanded = effectiveBudget.ancillary_income_section_expanded !== false;

  return (
    <div style={{
      backgroundColor: 'var(--bk-c-ffffff, #ffffff)',
      border: '1px solid var(--bk-c-e9eaeb, #E9EAEB)',
      borderRadius: '8px',
      boxShadow: '0px 1px 2px 0px rgba(10, 13, 18, 0.05)',
      padding: isMobile ? '12px' : '16px'
    }}>
      <h2 style={{
        fontSize: isMobile ? '18px' : '20px',
        fontWeight: '600',
        color: 'var(--bk-c-181d27, #181D27)',
        margin: '0 0 16px 0'
      }}>Income</h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
        <TicketsSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={effectiveBudget}
          calculated={calculated}
          updateBudget={updateBudget}
          createTicketBudgetLineItem={createTicketBudgetLineItem}
          deleteBudgetLineItem={deleteBudgetLineItem}
          autoSaveBudgetLineItem={autoSaveBudgetLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          expanded={ticketsExpanded}
          onToggleExpand={() => onVisibilityChange(
            'tickets_section_expanded', !ticketsExpanded)}
          settlementTickets={settlementTickets}
          showOfferColumn={effectiveBudget.tickets_show_offer !== false}
          showForecastColumn={effectiveBudget.tickets_show_forecast !== false}
          showActualsColumn={effectiveBudget.tickets_show_actuals !== false}
          hideCollapsedColumns={isMobile && mobileOfferColumnOnly}
          onColumnVisibilityChange={(setting, value) =>
            onVisibilityChange(`tickets_${setting.replace('_column', '')}`, value)}
          sectionTabIndexOffset={0}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={onReorderLineItems}
          allLineItems={allLineItems}
        />

        <BudgetLineItemSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={effectiveBudget}
          calculated={calculated}
          updateBudget={updateBudget}
          createBudgetLineItem={createIncomeBudgetLineItem}
          deleteBudgetLineItem={deleteBudgetLineItem}
          autoSaveBudgetLineItem={autoSaveBudgetLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          expanded={otherIncomeExpanded}
          onToggleExpand={() => onVisibilityChange(
            'other_income_section_expanded', !otherIncomeExpanded)}
          sectionTitle="Other Income"
          sectionSubtitle="Additional revenue shown on the offer/settlement that does count toward the artist split. Examples include shared VIP revenue, sponsorship allocations, or other income tied directly to the performance."
          lineItemsKey="income_line_items"
          lineItemsFilter={(item) => item.show_in_offer !== false}
          allowOfferVisibilityToggle={false}
          calculatedSectionKey="revenueSection"
          calculatedTotalKey="externalTotal"
          calculatedForecastTotalKey="externalForecastTotal"
          calculatedActualTotalKey="externalActualTotal"
          calculatedRightTotalKey="externalRightTotal"
          calculatedRightForecastTotalKey="externalRightForecastTotal"
          calculatedRightActualTotalKey="externalRightActualTotal"
          addButtonText="Add other income"
          showOfferColumn={effectiveBudget.other_income_show_offer !== false}
          showForecastColumn={effectiveBudget.other_income_show_forecast !== false}
          showActualsColumn={effectiveBudget.other_income_show_actuals !== false}
          hideCollapsedColumns={isMobile && mobileOfferColumnOnly}
          onColumnVisibilityChange={(setting, value) =>
            onVisibilityChange(`other_income_${setting.replace('_column', '')}`, value)}
          financeCategories={financeCategories}
          onFinanceCategoryAdded={onFinanceCategoryAdded}
          performers={performers}
          sectionTabIndexOffset={1000}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={onReorderLineItems}
          allLineItems={allLineItems}
        />

        {/* The passed-through tax stays listed on this card (O-4777). It is still a
            before-adjusted line item — the toggle only changes how it is applied —
            and hiding it made it uneditable and impossible to unmark. It is
            excluded from the section Total instead, and shown on its own line
            beneath, since it is added on top rather than deducted. getCalculatedRow
            resolves it from admissionTaxSection so it displays its real amount
            rather than the $0.00 that originally motivated hiding it. */}
        <BudgetLineItemSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={effectiveBudget}
          calculated={calculated}
          updateBudget={updateBudget}
          createBudgetLineItem={createBeforeAdjustedDeductionsBudgetLineItem}
          deleteBudgetLineItem={deleteBudgetLineItem}
          autoSaveBudgetLineItem={autoSaveBudgetLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          expanded={beforeAdjustedDeductionsExpanded}
          onToggleExpand={() => onVisibilityChange(
            'before_adjusted_deductions_section_expanded',
            !beforeAdjustedDeductionsExpanded)}
          sectionTitle="Before Adjusted Deductions"
          sectionSubtitle="Net Box Office Receipts minus any per-ticket deductions (such as Facilities Maintenance Fees). This section shows the adjusted box office amount before final settlement deductions are applied."
          lineItemsKey="before_adjusted_deductions_line_items"
          calculatedSectionKey="beforeAdjustedDeductionsSection"
          addButtonText="Add before adjusted deduction"
          showInlineRowToggles
          allowOfferVisibilityToggle={false}
          // Without this a row already saved show_in_offer: false — like the
          // marked tax on offer 907 — can be seen and edited here but never put
          // back on the offer, since this card has no lineItemsFilter to move it
          // to a sibling the way the two Expenses cards partition. (O-4775)
          showInlineVisibilityToggle
          onPassThroughChange={onPassThroughChange}
          showOfferColumn={effectiveBudget.before_adjusted_deductions_show_offer !== false}
          showForecastColumn={effectiveBudget.before_adjusted_deductions_show_forecast !== false}
          showActualsColumn={effectiveBudget.before_adjusted_deductions_show_actuals !== false}
          hideCollapsedColumns={isMobile && mobileOfferColumnOnly}
          onColumnVisibilityChange={(setting, value) =>
            onVisibilityChange(`before_adjusted_deductions_${setting.replace('_column', '')}`, value)}
          financeCategories={financeCategories}
          onFinanceCategoryAdded={onFinanceCategoryAdded}
          performers={performers}
          sectionTabIndexOffset={2000}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={onReorderLineItems}
          allLineItems={allLineItems}
        />

        {(() => {
          const informationalTax = calculated?.beforeAdjustedDeductionsSection?.internalTotal || 0;
          if (!informationalTax) return null;
          const netGate = calculated?.showSummary?.netBoxOfficeReceipts || 0;
          const grossGate = netGate + informationalTax;
          const rowStyle = { display: 'flex', justifyContent: 'space-between', fontSize: '14px' };
          return (
            <div
              data-testid="gross-gate-summary"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '4px',
                padding: '12px 16px',
                backgroundColor: 'var(--bk-c-fafafa, #FAFAFA)',
                border: '1px solid var(--bk-c-e9eaeb, #E9EAEB)',
                borderRadius: '8px',
              }}
            >
              <div style={rowStyle}>
                <span>Net Gate</span>
                <span>{formatCalculatedNumber(netGate)}</span>
              </div>
              <div style={{ ...rowStyle, color: 'var(--bk-c-535862, #535862)' }}>
                <span>+ Taxes &amp; fees (informational)</span>
                <span>{formatCalculatedNumber(informationalTax)}</span>
              </div>
              <div
                style={{
                  ...rowStyle,
                  fontWeight: 600,
                  borderTop: '1px solid var(--bk-c-e9eaeb, #E9EAEB)',
                  paddingTop: '4px',
                }}
              >
                <span>Gross Gate</span>
                <span>{formatCalculatedNumber(grossGate)}</span>
              </div>
            </div>
          );
        })()}

        <BudgetLineItemSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={effectiveBudget}
          calculated={calculated}
          updateBudget={updateBudget}
          createBudgetLineItem={createAdditionBudgetLineItem}
          deleteBudgetLineItem={deleteBudgetLineItem}
          autoSaveBudgetLineItem={autoSaveBudgetLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          expanded={additionsExpanded}
          onToggleExpand={() => onVisibilityChange(
            'additions_section_expanded', !additionsExpanded)}
          sectionTitle="Additions"
          sectionSubtitle="Amounts paid to the artist on top of the deal, such as buyouts and reimbursements. They don’t count toward the split."
          lineItemsKey="addition_line_items"
          calculatedSectionKey="additionsSection"
          addButtonText="Add addition"
          showInlineRowToggles
          showOfferColumn={effectiveBudget.additions_show_offer ?? true}
          showForecastColumn={effectiveBudget.additions_show_forecast ?? true}
          showActualsColumn={effectiveBudget.additions_show_actuals ?? true}
          hideCollapsedColumns={isMobile && mobileOfferColumnOnly}
          onColumnVisibilityChange={(setting, value) =>
            onVisibilityChange(`additions_${setting.replace('_column', '')}`, value)}
          financeCategories={financeCategories}
          onFinanceCategoryAdded={onFinanceCategoryAdded}
          performers={performers}
          sectionTabIndexOffset={3000}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={onReorderLineItems}
          allLineItems={allLineItems}
        />

        <BudgetLineItemSection
          csrfToken={csrfToken}
          team={team}
          currentUser={currentUser}
          offer={offer}
          budget={effectiveBudget}
          calculated={calculated}
          updateBudget={updateBudget}
          createBudgetLineItem={createAncillaryIncomeBudgetLineItem}
          deleteBudgetLineItem={deleteBudgetLineItem}
          autoSaveBudgetLineItem={autoSaveBudgetLineItem}
          formatCalculatedNumber={formatCalculatedNumber}
          expanded={ancillaryIncomeExpanded}
          onToggleExpand={() => onVisibilityChange(
            'ancillary_income_section_expanded', !ancillaryIncomeExpanded)}
          sectionTitle="Ancillary Income"
          sectionSubtitle="Revenue that isn't shown on the offer or settlement and isn't part of any artist calculations. Typically venue-retained income like bar, concessions, or parking."
          lineItemsKey="income_line_items"
          lineItemsFilter={(item) => item.show_in_offer === false}
          allowOfferVisibilityToggle={false}
          calculatedSectionKey="revenueSection"
          calculatedTotalSectionKey="showSummary"
          calculatedTotalKey="ancillaryIncome"
          calculatedForecastTotalKey="ancillaryIncomeForecast"
          calculatedActualTotalKey="ancillaryIncomeActual"
          calculatedRightTotalKey="internalRightTotal"
          calculatedRightForecastTotalKey="internalRightForecastTotal"
          calculatedRightActualTotalKey="internalRightActualTotal"
          addButtonText="Add ancillary income"
          hasOfferColumn={false}
          showForecastColumn={effectiveBudget.ancillary_income_show_forecast !== false}
          showActualsColumn={effectiveBudget.ancillary_income_show_actuals !== false}
          hideCollapsedColumns={isMobile && mobileOfferColumnOnly}
          onColumnVisibilityChange={(setting, value) =>
            onVisibilityChange(`ancillary_income_${setting.replace('_column', '')}`, value)}
          financeCategories={financeCategories}
          onFinanceCategoryAdded={onFinanceCategoryAdded}
          performers={performers}
          sectionTabIndexOffset={4000}
          showCurrencyConversion={showCurrencyConversion}
          globalCurrency={globalCurrency}
          actualsItems={addOnActuals}
          reorderLineItem={reorderLineItem}
          onReorderLineItems={onReorderLineItems}
          allLineItems={allLineItems}
        />
      </div>
    </div>
  );
};

IncomeSection.propTypes = {
  csrfToken: PropTypes.string.isRequired,
  team: PropTypes.object.isRequired,
  currentUser: PropTypes.object.isRequired,
  offer: PropTypes.object.isRequired,
  budget: PropTypes.object.isRequired,
  calculated: PropTypes.object,
  updateBudget: PropTypes.func.isRequired,
  onPassThroughChange: PropTypes.func,
  createTicketBudgetLineItem: PropTypes.func.isRequired,
  createIncomeBudgetLineItem: PropTypes.func.isRequired,
  createAncillaryIncomeBudgetLineItem: PropTypes.func.isRequired,
  createBeforeAdjustedDeductionsBudgetLineItem: PropTypes.func.isRequired,
  createAdditionBudgetLineItem: PropTypes.func.isRequired,
  deleteBudgetLineItem: PropTypes.func.isRequired,
  autoSaveBudgetLineItem: PropTypes.func.isRequired,
  formatCalculatedNumber: PropTypes.func.isRequired,
  financeCategories: PropTypes.array,
  onFinanceCategoryAdded: PropTypes.func,
  settlementTickets: PropTypes.array,
  addOnActuals: PropTypes.object,
  performers: PropTypes.array,
  onVisibilityChange: PropTypes.func,
  showCurrencyConversion: PropTypes.bool,
  globalCurrency: PropTypes.object,
  reorderLineItem: PropTypes.func,
  onReorderLineItems: PropTypes.func,
  allLineItems: PropTypes.array,
  mobileOfferColumnOnly: PropTypes.bool
};

export default IncomeSection;
